import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Provider
} from "@angular/core";
import { UserAddress } from "@onsip/common/services/api/resources/userAddress/user-address";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from "@angular/forms";
import { BehaviorSubject, combineLatest, Observable, Subscription } from "rxjs";
import { map, take } from "rxjs/operators";
import { UserAddressService } from "@onsip/common/services/api/resources/userAddress/user-address.service";
import { E2eLocators } from "@onsip/e2e/configs/locators";

export interface GroupedUsers {
  letter: string;
  data: Array<UserAddress>;
}

const CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @angular-eslint/no-forward-ref
  useExisting: forwardRef(() => AlphabeticalSelectionListComponent),
  multi: true
};

@Component({
  selector: "onsip-alphabetical-selection-list",
  templateUrl: "./alphabetical-selection-list.component.html",
  styleUrls: ["./alphabetical-selection-list.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CONTROL_VALUE_ACCESSOR],
  standalone: false
})
export class AlphabeticalSelectionListComponent implements ControlValueAccessor, OnInit, OnDestroy {
  constructor(private userAddressService: UserAddressService) {}
  maxItemCount = 0;
  @Input() columnCount = 3;
  onTouched!: Function;
  formControl = new FormControl(<Array<UserAddress>>[], { nonNullable: true });
  searchUser = new BehaviorSubject("");
  allUsers!: Array<UserAddress>;
  E2eLocators = E2eLocators;
  protected unsubscriber = new Subscription();

  @Input() set searchData(data: string) {
    this.searchUser.next(data);
  }

  @Input() set selectAll(data: boolean) {
    this.onSelectAll(data);
  }

  @Input() set maxItem(data: number) {
    this.maxItemCount = data;
  }

  @Output() selectAllChange = new EventEmitter<boolean>();

  groupedUsers$: Observable<Array<GroupedUsers>> =
    this.userAddressService.currentOrganizationUserAddresses.pipe(
      map(users => this.groupUsers(users))
    );
  filteredUsers$: Observable<Array<GroupedUsers>> = combineLatest([
    this.searchUser,
    this.groupedUsers$
  ]).pipe(
    map(([query, users]) =>
      users
        .map(el => ({
          ...el,
          data: el.data.filter(({ name }) => name.toLowerCase().includes(query.toLowerCase()))
        }))
        .filter(({ data }) => !!data.length)
    )
  );

  ngOnInit(): void {
    this.trackSelectAll();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  selectMember(event: MatCheckboxChange, item: UserAddress): void {
    if (event?.checked) {
      this.formControl.patchValue([...this.formControl.value, item]);
    } else {
      const data = this.formControl.value.filter(
        ({ userAddressId }: UserAddress) => userAddressId !== item.userAddressId
      );
      this.formControl.patchValue(data);
    }
  }

  isCheckedUser(users: Array<UserAddress>, user: UserAddress): boolean {
    return users?.some(({ userAddressId }) => userAddressId === user.userAddressId);
  }

  registerOnChange(onChange: Function): void {
    this.unsubscriber.add(
      this.formControl.valueChanges.subscribe(user => {
        onChange(user);
      })
    );
  }

  registerOnTouched(onTouched: Function): void {
    this.onTouched = onTouched;
  }

  writeValue(value: Array<UserAddress>): void {
    this.formControl.setValue(value);
  }

  private groupUsers(users: Array<UserAddress>): Array<GroupedUsers> {
    this.allUsers = users;
    return Object.values(
      this.getSortedUsers(users).reduce((acc: Record<string, GroupedUsers>, user: UserAddress) => {
        const letter = user.name.charAt(0).toLowerCase();

        if (acc[letter]) {
          return {
            ...acc,
            [letter]: {
              letter,
              data: [...acc[letter].data, user]
            }
          };
        }

        return {
          ...acc,
          [letter]: {
            letter,
            data: [user]
          }
        };
      }, {} as Record<string, GroupedUsers>)
    );
  }

  private getSortedUsers(users: Array<UserAddress>): Array<UserAddress> {
    const members: Array<UserAddress> = JSON.parse(JSON.stringify(users));
    members.sort((a: UserAddress, b: UserAddress) => {
      if (a.name.toLowerCase() > b.name.toLowerCase()) {
        return 1;
      }

      if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return -1;
      }

      return 0;
    });

    return members;
  }

  private onSelectAll(allSelected: boolean): void {
    this.unsubscriber.add(
      this.userAddressService.currentOrganizationUserAddresses.pipe(take(1)).subscribe(users => {
        if (allSelected) {
          this.formControl.patchValue(users);
        } else {
          if (this.formControl.value?.length === this.allUsers?.length) {
            this.formControl.patchValue([]);
          }
        }
      })
    );
  }

  private trackSelectAll(): void {
    this.unsubscriber.add(
      combineLatest({
        usersState: this.userAddressService.currentOrganizationUserAddresses,
        usersControlValue: this.formControl.valueChanges
      }).subscribe(({ usersState, usersControlValue }) => {
        this.selectAllChange.emit(usersState.length === usersControlValue?.length);
      })
    );
  }
}
